/**
 * How much money do I have — this morning.
 *
 * Owner, Sep 28 2026: "Daily 'how much money do I have' check." Four numbers
 * from GET /api/money/today: what is in the bank (Plaid balances, pulled after
 * 6 AM each day), what customers still owe, what the crew is owed, and the
 * month so far after every cost. Sits on the Dashboard and the Profit page.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RefreshCw, MessageSquare } from 'lucide-react';
import { api } from '../services/api';
import { parseDbTs } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';
import { invalidateMoney } from '../utils/moneyQueries';

const money = (n: any) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const ago = (iso?: string | null) => {
  // bank_balances.as_of is a naive UTC timestamp string; new Date() would read it as device-local time.
  const d = parseDbTs(iso);
  if (!d) return '';
  const ms = Math.max(0, Date.now() - d.getTime());
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function MoneyToday({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data, isLoading, isError } = useQuery('money-today', () => api.get('/money/today').then(r => r.data), { staleTime: 60_000, retry: false });
  const sync = useMutation(() => api.post('/money/sync').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Synced'); invalidateMoney(qc); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Sync failed'); },
  });
  const textMe = useMutation(() => api.post('/money/text-me').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Sent', { autoClose: 12000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that'); },
  });

  if (isLoading) return <div className="card p-4 text-sm text-gray-500">Counting the money…</div>;
  if (isError || !data) return <div className="card p-4 text-sm text-amber-700">Could not read the money right now.</div>;

  const bank = data.bank;
  const m = data.month_so_far || {};
  const monthName = m.month ? new Date(`${m.month}-02T12:00:00`).toLocaleDateString('en-US', { month: 'long' }) : 'This month';
  const noBank = !data.connections?.plaid_configured || !(data.connections?.banks || []).length;

  const Box = ({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode; tone?: string }) => (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-xl sm:text-2xl font-black leading-tight ${tone || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 truncate">{sub}</p>}
    </div>
  );

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-bold text-gray-900">Money this morning <span className="font-normal text-gray-500">· {data.date}</span></p>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => sync.mutate()} disabled={readOnly || sync.isLoading} title={readOnly ? 'View-only account' : undefined}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-semibold disabled:opacity-50 flex items-center gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${sync.isLoading ? 'animate-spin' : ''}`} /> {sync.isLoading ? 'Pulling…' : 'Pull the bank now'}
          </button>
          <button type="button" onClick={() => textMe.mutate()} disabled={readOnly || textMe.isLoading} title={readOnly ? 'View-only account' : 'Text me this now'}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-semibold disabled:opacity-50 flex items-center gap-1">
            <MessageSquare className="h-3.5 w-3.5" /> Text me
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Box label="In the bank" value={bank && bank.available != null ? money(bank.available) : '—'}
          tone={bank ? 'text-gray-900' : 'text-gray-400'}
          sub={bank ? <>{bank.cards_owed > 0 ? `cards owe ${money(bank.cards_owed)} · ` : ''}{bank.as_of ? `as of ${ago(bank.as_of)}` : ''}</> : (noBank ? <Link to="/settings#bank-accounts" className="underline">connect the bank in Settings → Money</Link> : 'not pulled yet — press Pull')} />
        <Box label="Owed to you" value={money(data.owed_to_you?.total)}
          sub={`${data.owed_to_you?.count || 0} bill${data.owed_to_you?.count === 1 ? '' : 's'} out${data.owed_to_you?.overdue_count ? ` · ${data.owed_to_you.overdue_count} overdue` : ''}`}
          tone={data.owed_to_you?.overdue_count ? 'text-amber-700' : 'text-gray-900'} />
        <Box label="You owe the crew" value={money(data.you_owe_crew?.total)}
          sub={data.you_owe_crew?.people ? `${data.you_owe_crew.people} ${data.you_owe_crew.people === 1 ? 'person' : 'people'}` : ''}
          tone={Number(data.you_owe_crew?.total) > 0 ? 'text-gray-900' : 'text-gray-400'} />
        <Box label={`${monthName} so far`} value={`${m.profit_before_owner < 0 ? '−' : ''}${money(Math.abs(m.profit_before_owner || 0))}`}
          tone={m.profit_before_owner >= 0 ? 'text-green-700' : 'text-red-600'}
          sub={<>after every cost · {money(m.paid)} of {money(m.revenue)} paid{m.unsorted_count ? ` · ${m.unsorted_count} unsorted` : ''}</>} />
      </div>
      {!compact && (
        <p className="text-[11px] text-gray-500">
          {data.net != null ? <>Bank + owed to you − crew − cards = <b className="text-gray-700">{money(data.net)}</b>. </> : null}
          Pulled from the bank after 6 AM every day{noBank ? ' once a bank is connected' : ''}. <Link to="/profit" className="underline">The whole month</Link>.
        </p>
      )}
    </div>
  );
}
